import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pencil, Reply, Trash2, Copy, SmilePlus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Fixed-position context menu for a DM/channel message (right-click / long-press).
 */
export default function DmMessageContextMenu({
  open,
  x,
  y,
  onClose,
  canReply,
  canEdit,
  canDelete,
  canCopy,
  canReact = false,
  reactEmojis = [],
  onReply,
  onEdit,
  onDelete,
  onCopy,
  onReact,
}) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [showReactPicker, setShowReactPicker] = useState(false);

  useLayoutEffect(() => {
    if (!open || !menuRef.current) {
      setPos({ left: x, top: y });
      return;
    }
    const rect = menuRef.current.getBoundingClientRect();
    const pad = 8;
    let left = x;
    let top = y;
    if (left + rect.width > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - rect.width - pad);
    }
    if (top + rect.height > window.innerHeight - pad) {
      top = Math.max(pad, window.innerHeight - rect.height - pad);
    }
    setPos({ left, top });
  }, [open, x, y, showReactPicker]);

  useEffect(() => {
    if (!open) {
      setShowReactPicker(false);
      return;
    }
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    const onPointer = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    const onScroll = () => onClose();
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("touchstart", onPointer, { passive: true });
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("touchstart", onPointer);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, onClose]);

  if (!open) return null;

  const items = [];
  if (canReply) {
    items.push({
      key: "reply",
      label: "Reply",
      icon: Reply,
      onSelect: onReply,
    });
  }
  if (canReact && Array.isArray(reactEmojis) && reactEmojis.length > 0) {
    items.push({
      key: "react",
      label: "React",
      icon: SmilePlus,
      onSelect: () => setShowReactPicker(true),
      keepOpen: true,
    });
  }
  if (canCopy) {
    items.push({
      key: "copy",
      label: "Copy text",
      icon: Copy,
      onSelect: onCopy,
    });
  }
  if (canEdit) {
    items.push({
      key: "edit",
      label: "Edit",
      icon: Pencil,
      onSelect: onEdit,
    });
  }
  if (canDelete) {
    items.push({
      key: "delete",
      label: "Delete",
      icon: Trash2,
      destructive: true,
      onSelect: onDelete,
    });
  }

  if (items.length === 0) return null;

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label="Message actions"
      className="fixed z-[100] min-w-[10rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
      style={{ left: pos.left, top: pos.top }}
    >
      {showReactPicker ? (
        <div className="flex gap-0.5 p-1" role="group" aria-label="React with emoji">
          {reactEmojis.map((emoji) => (
            <button
              key={emoji}
              type="button"
              role="menuitem"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-base hover:bg-accent"
              onClick={() => {
                onReact?.(emoji);
                onClose();
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      ) : (
        items.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={cn(
                "relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-2.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
                item.destructive &&
                  "text-destructive hover:text-destructive focus:text-destructive"
              )}
              onClick={() => {
                item.onSelect?.();
                if (!item.keepOpen) onClose();
              }}
            >
              <Icon className="h-3.5 w-3.5" />
              {item.label}
            </button>
          );
        })
      )}
    </div>,
    document.body
  );
}

/**
 * Wraps a message column and opens the parent menu on right-click / long-press.
 * Safe to use inside .map() (hook lives on this component instance).
 */
export function DmMessageContextTarget({ enabled, onOpen, children, className }) {
  const timerRef = useRef(null);
  const startRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => () => clearTimer(), []);

  if (!enabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={className}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen({ x: e.clientX, y: e.clientY });
      }}
      onTouchStart={(e) => {
        const touch = e.touches?.[0];
        if (!touch) return;
        startRef.current = { x: touch.clientX, y: touch.clientY };
        clearTimer();
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          onOpen({ x: touch.clientX, y: touch.clientY });
        }, 500);
      }}
      onTouchMove={(e) => {
        if (!timerRef.current || !startRef.current) return;
        const touch = e.touches?.[0];
        if (!touch) return;
        const dx = Math.abs(touch.clientX - startRef.current.x);
        const dy = Math.abs(touch.clientY - startRef.current.y);
        if (dx > 10 || dy > 10) clearTimer();
      }}
      onTouchEnd={() => {
        clearTimer();
        startRef.current = null;
      }}
      onTouchCancel={() => {
        clearTimer();
        startRef.current = null;
      }}
    >
      {children}
    </div>
  );
}
